import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';

interface BulkUpdateData {
  entityType: string;
  entityId: string;
  enumOptionIds: string[];
}

/**
 * Hook for managing rebate rule tag mutations
 * Handles adding and removing tags for rebate rules with proper cache invalidation
 */
export function useRebateRuleTagMutations() {
  const queryClient = useQueryClient();

  // Track pending mutations per rule (for loading states)
  const [tagsUpdatingByRuleId, setTagsUpdatingByRuleId] = useState<
    Map<string, boolean>
  >(new Map());

  // Generic mutation for updating rule tags
  const updateRuleTagsMutation = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    successMessage: 'Tags updated successfully',
    errorMessage: 'Failed to update tags',
    options: {
      onSuccess: (_, variables) => {
        const ruleId = variables.entityId;
        // Invalidate individual query
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', 'rebate_rule', ruleId],
        });
        // Invalidate bulk query to update UI
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
        setTagsUpdatingByRuleId((prev) => {
          const next = new Map(prev);
          next.set(ruleId, false);
          return next;
        });
      },
      onError: (_, variables) => {
        const ruleId = variables.entityId;
        setTagsUpdatingByRuleId((prev) => {
          const next = new Map(prev);
          next.set(ruleId, false);
          return next;
        });
      },
    },
  });

  const handleRuleTagAdd = useCallback(
    (
      ruleId: string,
      tagId: string,
      currentTags: CoreEnumOptionAssociation[],
    ) => {
      const currentTagIds = new Set(currentTags.map((t) => t.coreEnumOptionId));

      if (!currentTagIds.has(tagId)) {
        setTagsUpdatingByRuleId((prev) => {
          const next = new Map(prev);
          next.set(ruleId, true);
          return next;
        });

        updateRuleTagsMutation.mutate({
          entityType: 'rebate_rule',
          entityId: ruleId,
          enumOptionIds: [...currentTagIds, tagId],
        });
      }
    },
    [updateRuleTagsMutation],
  );

  const handleRuleTagRemove = useCallback(
    (
      ruleId: string,
      tagId: string,
      currentTags: CoreEnumOptionAssociation[],
    ) => {
      const currentTagIds = Array.from(
        new Set(currentTags.map((t) => t.coreEnumOptionId)),
      ).filter((id) => id !== tagId);

      setTagsUpdatingByRuleId((prev) => {
        const next = new Map(prev);
        next.set(ruleId, true);
        return next;
      });

      updateRuleTagsMutation.mutate({
        entityType: 'rebate_rule',
        entityId: ruleId,
        enumOptionIds: currentTagIds,
      });
    },
    [updateRuleTagsMutation],
  );

  return {
    handleRuleTagAdd,
    handleRuleTagRemove,
    tagsUpdatingByRuleId,
  };
}
